import {
  LearningManagement_V1,
  SessionManagement_V1,
} from "@rw1/auto-generated-clients";
import {
  ActivityMetrics,
  CountryCertificationsEntry,
  CountryHoursEntry,
  CountrySessionEntry,
  CumulativeMetricSeries,
  Dimension,
  ExperienceMetricPoint,
  ExperienceRating,
  ExperienceRatingSeries,
  ExternalMetricQuery,
  ExternalRatingQuery,
  LicenseRating,
  LicenseRatingSeries,
  MetricPoint,
  MetricQuery,
  MetricSeries,
  RatingQuery,
  RatingType,
  RatingValue,
} from "../models/metrics";
import { toSafeNumber, toSafeString } from "../primitives";

// ---------------------------------------------------------------------------
// Dimension
// ---------------------------------------------------------------------------

const DIMENSION_BY_API_VALUE: Record<string, Dimension> = {
  [SessionManagement_V1.Dimension.Week]: Dimension.WEEK,
  [SessionManagement_V1.Dimension.Month]: Dimension.MONTH,
  [SessionManagement_V1.Dimension.Quarter]: Dimension.QUARTER,
  [SessionManagement_V1.Dimension.Year]: Dimension.YEAR,
};

/**
 * Parses a dimension from an untrusted source — an API response, or a value
 * rehydrated from session storage (which is stored with no validation).
 *
 * Returns `null` for anything unrecognized so callers fall back to their own
 * default rather than sending a garbage dimension to the API or indexing a
 * `Record<Dimension, …>` with a missing key.
 */
export const parseDimension = (raw: unknown): Dimension | null =>
  typeof raw === "string" ? (DIMENSION_BY_API_VALUE[raw] ?? null) : null;

export const toApiSessionDimension = (
  dimension: Dimension,
): SessionManagement_V1.Dimension => {
  switch (dimension) {
    case Dimension.WEEK:
      return SessionManagement_V1.Dimension.Week;
    case Dimension.MONTH:
      return SessionManagement_V1.Dimension.Month;
    case Dimension.QUARTER:
      return SessionManagement_V1.Dimension.Quarter;
    case Dimension.YEAR:
      return SessionManagement_V1.Dimension.Year;
  }
};

export const toApiLearningDimension = (
  dimension: Dimension,
): LearningManagement_V1.Dimension => {
  switch (dimension) {
    case Dimension.WEEK:
      return LearningManagement_V1.Dimension.Week;
    case Dimension.MONTH:
      return LearningManagement_V1.Dimension.Month;
    case Dimension.QUARTER:
      return LearningManagement_V1.Dimension.Quarter;
    case Dimension.YEAR:
      return LearningManagement_V1.Dimension.Year;
  }
};

// ---------------------------------------------------------------------------
// RatingType
// ---------------------------------------------------------------------------

const RATING_TYPE_BY_API_VALUE: Record<string, RatingType> = {
  [LearningManagement_V1.RatingType.Practice]: RatingType.PRACTICE,
  [LearningManagement_V1.RatingType.Assessment]: RatingType.ASSESSMENT,
};

/**
 * Parses a rating type from an API response.
 *
 * Returns `null` for unrecognized values; callers decide whether an unknown
 * rating type should be skipped or counted, rather than having it silently
 * collapse into one of the known kinds.
 */
export const parseRatingType = (raw: unknown): RatingType | null =>
  typeof raw === "string" ? (RATING_TYPE_BY_API_VALUE[raw] ?? null) : null;

export const toApiRatingType = (
  ratingType: RatingType,
): LearningManagement_V1.RatingType => {
  switch (ratingType) {
    case RatingType.PRACTICE:
      return LearningManagement_V1.RatingType.Practice;
    case RatingType.ASSESSMENT:
      return LearningManagement_V1.RatingType.Assessment;
  }
};

// ---------------------------------------------------------------------------
// Country and experience breakdowns
// ---------------------------------------------------------------------------

export const toCountrySessionEntry = (
  dataPoint: SessionManagement_V1.CountryDataPoint,
): CountrySessionEntry => ({
  country: dataPoint.country,
  sessionCount: toSafeNumber(dataPoint.userSessions),
});

export const toCountryHoursEntry = (
  dataPoint: SessionManagement_V1.CountryDataPoint,
): CountryHoursEntry => ({
  country: dataPoint.country,
  hoursInVr: toSafeNumber(dataPoint.hoursInVr),
});

export const toCountryCertificationsEntry = (
  dataPoint: SessionManagement_V1.CountryDataPoint,
): CountryCertificationsEntry => ({
  country: dataPoint.country,
  certifications: toSafeNumber(dataPoint.certifications),
});

export const toExperienceMetricPoint = (
  dataPoint: SessionManagement_V1.ExperienceDataPoint,
): ExperienceMetricPoint => ({
  experienceId: dataPoint.contentMetadataId,
  experienceName: toSafeString(dataPoint.contentMetadataFriendlyName),
  uniqueUsers: toSafeNumber(dataPoint.uniqueUsers),
  hoursInVr: toSafeNumber(dataPoint.hoursInVr),
  userSessions: toSafeNumber(dataPoint.userSessions),
  certifications: toSafeNumber(dataPoint.certifications),
});

// ---------------------------------------------------------------------------
// Rating values
// ---------------------------------------------------------------------------

export const toRatingValueOrNull = (
  value: LearningManagement_V1.RatingTypeValue,
): RatingValue | null => {
  const ratingType = parseRatingType(value.ratingType);

  if (ratingType === null) {
    return null;
  }

  return {
    ratingType,
    value: value.value,
    count: value.count,
  };
};

export const toRatingValues = (
  values: LearningManagement_V1.RatingTypeValue[] | undefined,
): RatingValue[] =>
  (values ?? [])
    .map(toRatingValueOrNull)
    .filter((value): value is RatingValue => value !== null);

export const toExperienceRating = (
  dataPoint: LearningManagement_V1.RatingExperienceDataPoint,
): ExperienceRating => ({
  experienceId: dataPoint.contentMetadataId,
  experienceName: toSafeString(dataPoint.contentMetadataFriendlyName),
  values: toRatingValues(dataPoint.values),
});

export const toExperienceRatingSeries = (
  result: LearningManagement_V1.RatingMetricResult | null | undefined,
): ExperienceRatingSeries => ({
  experiences: Array.isArray(result?.dataPoints)
    ? result.dataPoints.map(toExperienceRating)
    : [],
  aggregateValues: toRatingValues(result?.aggregateValues),
});

export const toLicenseRating = (
  dataPoint: LearningManagement_V1.ExternalRatingExperienceDataPoint,
): LicenseRating => ({
  licenseId: dataPoint.licenseId,
  values: toRatingValues(dataPoint.values),
});

export const toLicenseRatingSeries = (
  result: LearningManagement_V1.ExternalRatingMetricResult | null | undefined,
): LicenseRatingSeries => ({
  licenses: Array.isArray(result?.dataPoints)
    ? result.dataPoints.map(toLicenseRating)
    : [],
  aggregateValues: toRatingValues(result?.aggregateValues),
});

// ---------------------------------------------------------------------------
// Metric series
// ---------------------------------------------------------------------------

export const toMetricPoint = (
  dataPoint: SessionManagement_V1.DataPoint,
): MetricPoint => ({
  periodStart: dataPoint.dimensionStartDate,
  value: dataPoint.value,
});

export const toMetricSeries = (
  result: SessionManagement_V1.MetricResult | null | undefined,
): MetricSeries => ({
  points: Array.isArray(result?.dataPoints)
    ? result.dataPoints.map(toMetricPoint)
    : [],
  aggregateValue: toSafeNumber(result?.aggregateValue),
});

const toActivityMetricSeries = (
  dataPoints: SessionManagement_V1.AllMetricsDataPoint[],
  readValue: (dataPoint: SessionManagement_V1.AllMetricsDataPoint) => number,
  aggregateValue: number,
): MetricSeries => ({
  points: dataPoints.map((dataPoint) => ({
    periodStart: dataPoint.dimensionStartDate,
    value: toSafeNumber(readValue(dataPoint)),
  })),
  aggregateValue,
});

export const toActivityMetrics = (
  result: SessionManagement_V1.AllAggregateMetricResult | null | undefined,
): ActivityMetrics => {
  const dataPoints = Array.isArray(result?.dataPoints) ? result.dataPoints : [];

  return {
    hoursInVr: toActivityMetricSeries(
      dataPoints,
      (dataPoint) => dataPoint.hoursInVrValue,
      toSafeNumber(result?.aggregateHoursInVrValue),
    ),
    uniqueUsers: toActivityMetricSeries(
      dataPoints,
      (dataPoint) => dataPoint.uniqueUsersValue,
      toSafeNumber(result?.aggregateUniqueUsersValue),
    ),
    userSessions: toActivityMetricSeries(
      dataPoints,
      (dataPoint) => dataPoint.userSessionsValue,
      toSafeNumber(result?.aggregateUserSessionsValue),
    ),
    certifications: toActivityMetricSeries(
      dataPoints,
      (dataPoint) => dataPoint.certificationsValue,
      toSafeNumber(result?.aggregateCertificationsValue),
    ),
  };
};

export const toCumulativeMetricSeries = (
  result: SessionManagement_V1.CumulativeMetricsResult | null | undefined,
): CumulativeMetricSeries => ({
  hoursInVr: toMetricSeries(result?.hoursInVr),
  uniqueUsers: toMetricSeries(result?.uniqueUsers),
  userSessions: toMetricSeries(result?.userSessions),
});

// ---------------------------------------------------------------------------
// Outbound request filters
// ---------------------------------------------------------------------------

const SECONDS_PER_MINUTE = 60;
const DISPLAY_ROUNDS_UP_AT_SECONDS = SECONDS_PER_MINUTE / 2;
const LOWEST_DISPLAYED_MINUTES = 1;
const KEEP_EVERY_SESSION = 0;

const shortestSecondsDisplayingAs = (minutes: number): number =>
  minutes * SECONDS_PER_MINUTE - DISPLAY_ROUNDS_UP_AT_SECONDS;

export const toMinSessionDurationSeconds = (
  minutes: number | undefined,
): number | undefined => {
  if (minutes === undefined) return undefined;
  if (minutes <= LOWEST_DISPLAYED_MINUTES) return KEEP_EVERY_SESSION;

  return shortestSecondsDisplayingAs(minutes);
};

export const toApiMetricFilter = (
  query: MetricQuery,
): SessionManagement_V1.MetricFilter =>
  new SessionManagement_V1.MetricFilter({
    startDate: query.startDate,
    endDate: query.endDate,
    dimension: toApiSessionDimension(query.dimension),
    contentMetadataIds: query.contentMetadataIds,
    countries: query.countries,
    minSessionDurationInSeconds: toMinSessionDurationSeconds(
      query.minSessionDurationInMinutes,
    ),
  });

export const toApiExternalMetricFilter = (
  query: ExternalMetricQuery,
): SessionManagement_V1.ExternalMetricFilter =>
  new SessionManagement_V1.ExternalMetricFilter({
    startDate: query.startDate,
    endDate: query.endDate,
    dimension: toApiSessionDimension(query.dimension),
    licenseIds: query.licenseIds,
    minSessionDurationInSeconds: toMinSessionDurationSeconds(
      query.minSessionDurationInMinutes,
    ),
  });

export const toApiDateFilter = (range: {
  startDate: Date;
  endDate: Date;
}): SessionManagement_V1.DateFilter =>
  new SessionManagement_V1.DateFilter({
    startDate: range.startDate,
    endDate: range.endDate,
  });

export const toApiUserLevelMetricFilter = (query: {
  startDate: Date;
  endDate: Date;
  minSessionDurationInMinutes?: number;
}): SessionManagement_V1.UserLevelMetricFilter =>
  new SessionManagement_V1.UserLevelMetricFilter({
    startDate: query.startDate,
    endDate: query.endDate,
    minSessionDurationInSeconds: toMinSessionDurationSeconds(
      query.minSessionDurationInMinutes,
    ),
  });

export const toApiLearningMetricFilter = (
  query: RatingQuery,
): LearningManagement_V1.MetricFilter =>
  new LearningManagement_V1.MetricFilter({
    startDate: query.startDate,
    endDate: query.endDate,
    contentMetadataIds: query.contentMetadataIds,
    countries: query.countries,
  });

export const toApiLearningExternalMetricFilter = (
  query: ExternalRatingQuery,
): LearningManagement_V1.ExternalMetricFilter =>
  new LearningManagement_V1.ExternalMetricFilter({
    startDate: query.startDate,
    endDate: query.endDate,
    licenseIds: query.licenseIds,
  });
